import * as React from 'react';
import { graphql } from 'react-apollo';

import type Maybe from 'graphql/tsutils/Maybe';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { SpinnerIcon } from '@coursera/cds-icons';

import ProgramMembershipLimitQuery from 'bundles/internal-enterprise/components/api/ProgramMembershipLimit.graphql';
import type {
  ProgramMembershipLimitQuery as ProgramMembershipLimitQueryData,
  ProgramMembershipLimitQueryVariables,
} from 'bundles/internal-enterprise/components/api/__generated__/ProgramMembershipLimit';

import _t from 'i18n!nls/internal-enterprise';

import 'css!./__styles__/ProgramMembershipLimit';

type PropsFromCaller = {
  contractId: string;
};

type PropsFromGraphQl = {
  enterpriseContractUtilization?: {
    id: string;
    contractLimitType: string;
    totalUserLicense: Maybe<number>;
    remainingUserLicense: Maybe<number>;
    totalEnrollment: Maybe<number>;
    remainingEnrollment: Maybe<number>;
  };
  loading: boolean;
  error: boolean;
};

type Props = PropsFromCaller & PropsFromGraphQl;

class ProgramMembershipLimit extends React.Component<Props> {
  render() {
    const { enterpriseContractUtilization, loading, error } = this.props;
    if (error) {
      return (
        <div>
          <FormattedMessage message={_t('Something went wrong')} />
        </div>
      );
    }
    if (loading) {
      return <SpinnerIcon />;
    }
    const { contractLimitType, remainingUserLicense, remainingEnrollment } = enterpriseContractUtilization ?? {};
    const count = remainingUserLicense ?? 0 + (remainingEnrollment ?? 0);
    return (
      <div className="rc-ProgramMembershipLimit">
        {!(
          contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_INVALID' ||
          contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_NO_LIMIT'
        ) &&
          count > 0 && (
            <div>
              <FormattedMessage message={_t('{count} enrollments remaining')} count={count} />
            </div>
          )}
        {!(
          contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_INVALID' ||
          contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_NO_LIMIT'
        ) &&
          count === 0 && (
            <div className="warn">
              <FormattedMessage message={_t('WARNING: Enrollment limit reached')} />
            </div>
          )}
        {contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_NO_LIMIT' && (
          <div>
            <FormattedMessage message={_t('No enrollment limit')} />
          </div>
        )}
        {contractLimitType === 'ENTERPRISE_CONTRACT_LIMIT_TYPE_INVALID' && (
          <div>
            <FormattedMessage message={_t('Error validating enrollment limit type')} />
          </div>
        )}
      </div>
    );
  }
}

export default graphql<
  PropsFromCaller,
  ProgramMembershipLimitQueryData,
  ProgramMembershipLimitQueryVariables,
  PropsFromGraphQl
>(ProgramMembershipLimitQuery, {
  options: ({ contractId }) => ({
    variables: {
      contractId,
    },
    context: { clientName: 'gatewayGql' },
  }),
  props: ({ data }) => {
    const fields = data?.EnterpriseContractUtilizations?.queryByContractId;
    if (!fields) {
      return {
        loading: data?.loading ?? false,
        error: Boolean(data?.error),
      };
    }
    return {
      enterpriseContractUtilization: {
        id: fields.id,
        contractLimitType: fields.contractLimitType,
        totalUserLicense: fields.totalUserLicense,
        remainingUserLicense: fields.remainingUserLicense,
        totalEnrollment: fields.totalEnrollment,
        remainingEnrollment: fields.remainingEnrollment,
      },
      loading: false,
      error: false,
    };
  },
})(ProgramMembershipLimit);
